import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Paper, Tabs, Tab, TextField, MenuItem, Button, Grid,
    Table, TableHead, TableRow, TableCell, TableBody, Chip, Dialog,
    DialogTitle, DialogContent, DialogActions, Card, CardContent, Badge,
    IconButton, Divider, Alert, FormControl, InputLabel, Select
} from '@mui/material';
import {
    Notifications as NotificationsIcon,
    Print as PrintIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    Visibility as ViewIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import AlternativeArrangement from '../components/AlternativeArrangement';
import { leaveWorkflowAPI, staffAPI } from '../services/api';

const getUserRole = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    return user?.role;
};

const LeaveManagement = () => {
    const printRef = useRef();
    const arrangementRef = useRef();
    const [tab, setTab] = useState(0);
    const [leaves, setLeaves] = useState([]);
    const [myLeaves, setMyLeaves] = useState([]);
    const [substitutionRequests, setSubstitutionRequests] = useState([]);
    const [pendingApprovals, setPendingApprovals] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [leaveBalance, setLeaveBalance] = useState({});
    const [loading, setLoading] = useState(false);
    const role = getUserRole();

    // Apply Form States
    const [leaveType, setLeaveType] = useState('CL');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [totalDays, setTotalDays] = useState(1);
    const [reason, setReason] = useState('');

    // Approval Dialog
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedLeave, setSelectedLeave] = useState(null);
    const [remarks, setRemarks] = useState('');
    const [dialogMode, setDialogMode] = useState(''); // 'hod' or 'principal'

    // Notification Dialog
    const [notificationDialog, setNotificationDialog] = useState(false);

    // Print Alternative Arrangement Dialog
    const [printDialog, setPrintDialog] = useState(false);
    const [printLeaveData, setPrintLeaveData] = useState(null);

    // Manual Assign Dialog States
    const [openManageSubDialog, setOpenManageSubDialog] = useState(false);
    const [manageLeaveTarget, setManageLeaveTarget] = useState(null);
    const [staffList, setStaffList] = useState([]);
    const [selectedPeriodToAssign, setSelectedPeriodToAssign] = useState(null);
    const [selectedSubTeacher, setSelectedSubTeacher] = useState('');
    const [customSubjectName, setCustomSubjectName] = useState('');
    const [openManualAssignForm, setOpenManualAssignForm] = useState(false);

    const isAdmin = ['admin', 'principal', 'super_admin'].includes(role);

    useEffect(() => {
        fetchLeaveBalance();
        fetchMyLeaves();
        fetchSubstitutionRequests();
        fetchNotifications();
        fetchStaffList();
        if (isAdmin) {
            fetchPendingApprovals();
        }
    }, []);

    // Calculate total days when dates change
    useEffect(() => {
        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
            setTotalDays(diff > 0 ? diff : 1);
        }
    }, [startDate, endDate]);

    const fetchLeaveBalance = async () => {
        try {
            const res = await leaveWorkflowAPI.getBalance();
            if (res.data.success) setLeaveBalance(res.data.data);
        } catch (error) {
            console.error('Error fetching leave balance:', error);
        }
    };

    const fetchMyLeaves = async () => {
        try {
            const res = await leaveWorkflowAPI.getMyApplications();
            if (res.data.success) setMyLeaves(res.data.data);
        } catch (error) {
            console.error('Error fetching leaves:', error);
        }
    };

    const fetchSubstitutionRequests = async () => {
        try {
            const res = await leaveWorkflowAPI.getSubstitutionRequests();
            if (res.data.success) setSubstitutionRequests(res.data.data);
        } catch (error) {
            console.error('Error fetching substitution requests:', error);
        }
    };

    const fetchPendingApprovals = async () => {
        try {
            const res = await leaveWorkflowAPI.getForApproval();
            if (res.data.success) setPendingApprovals(res.data.data);
        } catch (error) {
            console.error('Error fetching approvals:', error);
        }
    };

    const fetchStaffList = async () => {
        try {
            const res = await staffAPI.getAll();
            if (res.data.success) setStaffList(res.data.data || []);
        } catch (error) {
            console.error('Error fetching staff list:', error);
        }
    };

    const fetchNotifications = async () => {
        try {
            const res = await leaveWorkflowAPI.getNotifications();
            if (res.data.success) setNotifications(res.data.data);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    };

    const handleApply = async () => {
        if (!startDate || !endDate || !reason) {
            toast.error('Please fill all required fields');
            return;
        }

        try {
            const res = await leaveWorkflowAPI.apply({ leaveType, startDate, endDate, totalDays, reason });
            if (res.data.success) {
                toast.success(res.data.message || 'Leave Applied Successfully');
                fetchMyLeaves();
                setTab(1);
                setStartDate('');
                setEndDate('');
                setReason('');
                setTotalDays(1);
            } else {
                toast.error(res.data.error || 'Failed to apply');
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error applying for leave');
        }
    };

    const handleConfirmSubstitution = async (substitutionId) => {
        try {
            const res = await leaveWorkflowAPI.confirmSubstitution(substitutionId, { remarks: '' });
            if (res.data.success) {
                toast.success('Substitution confirmed!');
                fetchSubstitutionRequests();
            } else {
                toast.error(res.data.error);
            }
        } catch (error) {
            toast.error('Error confirming substitution');
        }
    };

    const handleApproval = async (approved) => {
        if (!selectedLeave) return;

        try {
            const res = dialogMode === 'hod'
                ? await leaveWorkflowAPI.hodApprove(selectedLeave.id, { approved, remarks })
                : await leaveWorkflowAPI.principalApprove(selectedLeave.id, { approved, remarks });

            if (res.data.success) {
                toast.success(res.data.message);
                setOpenDialog(false);
                setSelectedLeave(null);
                setRemarks('');
                fetchPendingApprovals();
            } else {
                toast.error(res.data.error);
            }
        } catch (error) {
            toast.error('Error processing approval');
        }
    };

    const handleManualAssign = async (subId) => {
        if (!selectedSubTeacher) {
            toast.error('Please select a substitute teacher.');
            return;
        }
        try {
            const res = await leaveWorkflowAPI.manualAssignSubstitution(subId, {
                substituteTeacherId: selectedSubTeacher,
                subjectName: customSubjectName
            });
            if (res.data.success) {
                toast.success(res.data.message || 'Substitution manually assigned successfully!');
                const myLeavesRes = await leaveWorkflowAPI.getMyApplications();
                if (myLeavesRes.data.success) {
                    setMyLeaves(myLeavesRes.data.data);
                    const updatedLeave = myLeavesRes.data.data.find(l => l.id === manageLeaveTarget.id);
                    if (updatedLeave) setManageLeaveTarget(updatedLeave);
                }
                setOpenManualAssignForm(false);
                setSelectedPeriodToAssign(null);
                setSelectedSubTeacher('');
                setCustomSubjectName('');
            } else {
                toast.error(res.data.error || 'Failed to assign');
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error assigning substitution');
        }
    };

    const handleMarkPeriodAsBlank = async (subId) => {
        if (!window.confirm('Are you sure you want to mark this class as off/no lecture?')) return;
        try {
            const res = await leaveWorkflowAPI.markPeriodAsBlank(subId);
            if (res.data.success) {
                toast.success(res.data.message || 'Period marked as off/no lecture!');
                const myLeavesRes = await leaveWorkflowAPI.getMyApplications();
                if (myLeavesRes.data.success) {
                    setMyLeaves(myLeavesRes.data.data);
                    const updatedLeave = myLeavesRes.data.data.find(l => l.id === manageLeaveTarget.id);
                    if (updatedLeave) setManageLeaveTarget(updatedLeave);
                }
            } else {
                toast.error(res.data.error || 'Failed to mark as blank');
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error marking period blank');
        }
    };

    const handleForwardToHOD = async (leaveId) => {
        try {
            const res = await leaveWorkflowAPI.forwardToHOD(leaveId);
            if (res.data.success) {
                toast.success(res.data.message || 'Forwarded to HOD successfully!');
                fetchMyLeaves();
            } else {
                toast.error(res.data.error || 'Failed to forward to HOD');
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error forwarding to HOD');
        }
    };

    const getStatusColor = (stage) => {
        switch (stage) {
            case 'approved': return 'success';
            case 'rejected': return 'error';
            case 'principal_pending': return 'info';
            case 'hod_pending': return 'warning';
            default: return 'default';
        }
    };

    const getStatusLabel = (stage) => {
        switch (stage) {
            case 'substitution_pending': return 'Awaiting Substitutes';
            case 'hod_pending': return 'HOD Approval Pending';
            case 'principal_pending': return 'Principal Approval Pending';
            case 'approved': return 'Approved';
            case 'rejected': return 'Rejected';
            default: return stage;
        }
    };

    const unreadNotifications = notifications.filter(n => !n.isRead).length;

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Leave Management</Typography>
                <IconButton onClick={() => setNotificationDialog(true)}>
                    <Badge badgeContent={unreadNotifications} color="error">
                        <NotificationsIcon />
                    </Badge>
                </IconButton>
            </Box>

            {/* Leave Balance Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {leaveBalance.balance && Object.entries(leaveBalance.balance).map(([type, balance]) => (
                    <Grid item xs={6} md={2} key={type}>
                        <Card variant="outlined">
                            <CardContent sx={{ textAlign: 'center', py: 1 }}>
                                <Typography variant="caption" color="textSecondary">
                                    {type === 'CL' ? 'Casual' : type === 'SL' ? 'Sick' : type === 'EL' ? 'Earned' : type === 'ML' ? 'Medical' : 'Duty'}
                                </Typography>
                                <Typography variant="h5" color="primary">{balance}</Typography>
                                <Typography variant="caption">
                                    Used: {leaveBalance.used?.[type] || 0}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label="Apply Leave" />
                <Tab label="My Applications" />
                <Tab label={`Substitution Requests (${substitutionRequests.length})`} />
                {isAdmin && <Tab label={`Pending Approvals (${pendingApprovals.length})`} />}
            </Tabs>

            {/* Apply Leave Tab */}
            {tab === 0 && (
                <Paper sx={{ p: 3, maxWidth: 700 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                select fullWidth label="Leave Type"
                                value={leaveType}
                                onChange={e => setLeaveType(e.target.value)}
                            >
                                <MenuItem value="CL">Casual Leave (CL)</MenuItem>
                                <MenuItem value="SL">Sick Leave (SL)</MenuItem>
                                <MenuItem value="EL">Earned Leave (EL)</MenuItem>
                                <MenuItem value="ML">Medical Leave (ML)</MenuItem>
                                <MenuItem value="DL">Duty Leave (DL)</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                type="date" fullWidth label="Start Date"
                                InputLabelProps={{ shrink: true }}
                                value={startDate}
                                onChange={e => setStartDate(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                type="date" fullWidth label="End Date"
                                InputLabelProps={{ shrink: true }}
                                value={endDate}
                                onChange={e => setEndDate(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                type="number" fullWidth label="Total Days"
                                value={totalDays}
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                multiline rows={3} fullWidth label="Reason"
                                value={reason}
                                onChange={e => setReason(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                When you apply, other teachers will be notified to cover your periods.
                                Once all substitutions are confirmed, HOD will be notified for approval.
                            </Alert>
                            <Button variant="contained" onClick={handleApply}>Apply Leave</Button>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* My Applications Tab */}
            {tab === 1 && (
                <Paper>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Type</TableCell>
                                <TableCell>Dates</TableCell>
                                <TableCell>Days</TableCell>
                                <TableCell>Reason</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Substitutions</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {myLeaves.map(leave => (
                                <TableRow key={leave.id}>
                                    <TableCell>{leave.leaveType}</TableCell>
                                    <TableCell>{leave.startDate} to {leave.endDate}</TableCell>
                                    <TableCell>{leave.totalDays}</TableCell>
                                    <TableCell>{leave.reason?.substring(0, 50)}...</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getStatusLabel(leave.workflowStage)}
                                            color={getStatusColor(leave.workflowStage)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {leave.substitutions?.length > 0 && (
                                            <Typography variant="caption">
                                                {leave.substitutions.filter(s => s.substituteConfirmed).length}/{leave.substitutions.length} confirmed
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => {
                                                setPrintLeaveData(leave);
                                                setPrintDialog(true);
                                            }}
                                            title="View/Print Alternative Arrangement"
                                        >
                                            <PrintIcon fontSize="small" />
                                        </IconButton>
                                        {leave.workflowStage === 'substitution_pending' && (
                                            <>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                    sx={{ ml: 1 }}
                                                    onClick={() => {
                                                        setManageLeaveTarget(leave);
                                                        setOpenManageSubDialog(true);
                                                    }}
                                                >
                                                    Manage
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="warning"
                                                    sx={{ ml: 1 }}
                                                    onClick={() => handleForwardToHOD(leave.id)}
                                                >
                                                    Forward HOD
                                                </Button>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}

            {/* Substitution Requests Tab */}
            {tab === 2 && (
                <Paper>
                    {substitutionRequests.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography color="textSecondary">No substitution requests available</Typography>
                        </Box>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Teacher on Leave</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Period</TableCell>
                                    <TableCell>Time</TableCell>
                                    <TableCell>Class</TableCell>
                                    <TableCell>Subject</TableCell>
                                    <TableCell>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {substitutionRequests.map(sub => (
                                    <TableRow key={sub.id}>
                                        <TableCell>
                                            {sub.leaveApplication?.staff?.user?.firstName} {sub.leaveApplication?.staff?.user?.lastName}
                                        </TableCell>
                                        <TableCell>{sub.leaveDate}</TableCell>
                                        <TableCell>Period {sub.periodNumber}</TableCell>
                                        <TableCell>{sub.periodTime}</TableCell>
                                        <TableCell>{sub.class?.name} {sub.section?.name}</TableCell>
                                        <TableCell>{sub.subject?.name}</TableCell>
                                        <TableCell>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<CheckIcon />}
                                                onClick={() => handleConfirmSubstitution(sub.id)}
                                            >
                                                I'll Cover
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Paper>
            )}

            {/* Pending Approvals Tab (Admin/HOD/Principal) */}
            {tab === 3 && isAdmin && (
                <Paper>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Staff</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Dates</TableCell>
                                <TableCell>Days</TableCell>
                                <TableCell>Stage</TableCell>
                                <TableCell>Substitutions</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pendingApprovals.map(leave => (
                                <TableRow key={leave.id}>
                                    <TableCell>
                                        {leave.staff?.user?.firstName} {leave.staff?.user?.lastName}
                                    </TableCell>
                                    <TableCell>{leave.leaveType}</TableCell>
                                    <TableCell>{leave.startDate} to {leave.endDate}</TableCell>
                                    <TableCell>{leave.totalDays}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getStatusLabel(leave.workflowStage)}
                                            color={getStatusColor(leave.workflowStage)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {leave.substitutions?.map((s, i) => (
                                            <Typography key={i} variant="caption" display="block">
                                                P{s.periodNumber}: {s.substituteTeacher?.user?.firstName || 'Pending'} {s.substituteConfirmed ? '✓' : ''}
                                            </Typography>
                                        ))}
                                    </TableCell>
                                    <TableCell>
                                        {leave.workflowStage === 'hod_pending' && (
                                            <Button
                                                size="small" variant="outlined"
                                                onClick={() => {
                                                    setSelectedLeave(leave);
                                                    setDialogMode('hod');
                                                    setOpenDialog(true);
                                                }}
                                            >
                                                HOD Review
                                            </Button>
                                        )}
                                        {leave.workflowStage === 'principal_pending' && role === 'principal' && (
                                            <Button
                                                size="small" variant="outlined"
                                                onClick={() => {
                                                    setSelectedLeave(leave);
                                                    setDialogMode('principal');
                                                    setOpenDialog(true);
                                                }}
                                            >
                                                Principal Review
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}

            {/* Approval Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {dialogMode === 'hod' ? 'HOD Approval' : 'Principal Approval'}
                </DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        <strong>Staff:</strong> {selectedLeave?.staff?.user?.firstName} {selectedLeave?.staff?.user?.lastName}
                    </Typography>
                    <Typography gutterBottom>
                        <strong>Type:</strong> {selectedLeave?.leaveType} | <strong>Days:</strong> {selectedLeave?.totalDays}
                    </Typography>
                    <Typography gutterBottom>
                        <strong>Reason:</strong> {selectedLeave?.reason}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>Substitution Arrangements:</Typography>
                    {selectedLeave?.substitutions?.map((s, i) => (
                        <Typography key={i} variant="body2">
                            Period {s.periodNumber} ({s.class?.name}): {s.substituteTeacher?.user?.firstName || 'Not assigned'} {s.substituteConfirmed ? '✓' : ''}
                        </Typography>
                    ))}
                    <TextField
                        fullWidth multiline rows={2}
                        label="Remarks" margin="dense"
                        value={remarks}
                        onChange={e => setRemarks(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleApproval(false)} color="error" startIcon={<CloseIcon />}>
                        Reject
                    </Button>
                    <Button onClick={() => handleApproval(true)} color="success" variant="contained" startIcon={<CheckIcon />}>
                        Approve
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Notifications Dialog */}
            <Dialog open={notificationDialog} onClose={() => setNotificationDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Notifications</DialogTitle>
                <DialogContent>
                    {notifications.length === 0 ? (
                        <Typography color="textSecondary">No notifications</Typography>
                    ) : (
                        notifications.slice(0, 10).map(n => (
                            <Box 
                                key={n.id} 
                                sx={{ p: 1, borderBottom: '1px solid #eee', bgcolor: n.isRead ? 'transparent' : 'action.hover', cursor: n.isRead ? 'default' : 'pointer' }}
                                onClick={async () => {
                                    if (!n.isRead) {
                                        try {
                                            await leaveWorkflowAPI.markNotificationRead(n.id);
                                            fetchNotifications();
                                        } catch (err) {
                                            console.error(err);
                                        }
                                    }
                                }}
                            >
                                <Typography variant="subtitle2">{n.title}</Typography>
                                <Typography variant="body2" color="textSecondary">{n.message}</Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {new Date(n.createdAt).toLocaleString()}
                                </Typography>
                            </Box>
                        ))
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNotificationDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Manage Substitutions Dialog */}
            <Dialog open={openManageSubDialog} onClose={() => setOpenManageSubDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Manage Period Substitutions</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="subtitle1" gutterBottom>
                        Leave Period: {manageLeaveTarget?.startDate} to {manageLeaveTarget?.endDate}
                    </Typography>
                    <Table size="small" sx={{ mt: 2 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Period</TableCell>
                                <TableCell>Time</TableCell>
                                <TableCell>Class</TableCell>
                                <TableCell>Subject</TableCell>
                                <TableCell>Assigned Substitute</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {manageLeaveTarget?.substitutions?.map((sub) => (
                                <TableRow key={sub.id}>
                                    <TableCell>{sub.leaveDate}</TableCell>
                                    <TableCell>Period {sub.periodNumber}</TableCell>
                                    <TableCell>{sub.periodTime}</TableCell>
                                    <TableCell>{sub.class?.name} - {sub.section?.name}</TableCell>
                                    <TableCell>{sub.subjectName || sub.subject?.name || '-'}</TableCell>
                                    <TableCell>
                                        {sub.substituteTeacher?.user 
                                            ? `${sub.substituteTeacher.user.firstName} ${sub.substituteTeacher.user.lastName}` 
                                            : (sub.substituteRemarks || 'Unassigned')}
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={sub.substituteConfirmed ? "Confirmed" : "Pending"} 
                                            color={sub.substituteConfirmed ? "success" : "default"} 
                                            size="small" 
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {!sub.substituteConfirmed && (
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button 
                                                    size="small" 
                                                    variant="outlined" 
                                                    onClick={() => {
                                                        setSelectedPeriodToAssign(sub);
                                                        setCustomSubjectName(sub.subjectName || sub.subject?.name || '');
                                                        setOpenManualAssignForm(true);
                                                    }}
                                                >
                                                    Assign Manually
                                                </Button>
                                                <Button 
                                                    size="small" 
                                                    variant="outlined" 
                                                    color="error" 
                                                    onClick={() => handleMarkPeriodAsBlank(sub.id)}
                                                >
                                                    Mark Off/Blank
                                                </Button>
                                            </Box>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    {/* Manual Assignment Section inside Dialog */}
                    {openManualAssignForm && selectedPeriodToAssign && (
                        <Paper sx={{ p: 2, mt: 3, border: '1px dashed #ccc' }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Manually Assign Substitute for Period {selectedPeriodToAssign.periodNumber} ({selectedPeriodToAssign.periodTime})
                            </Typography>
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Substitute Teacher</InputLabel>
                                        <Select
                                            value={selectedSubTeacher}
                                            label="Substitute Teacher"
                                            onChange={(e) => setSelectedSubTeacher(e.target.value)}
                                        >
                                            {staffList.map((t) => (
                                                <MenuItem key={t.id} value={t.id}>
                                                    {t.user?.firstName} {t.user?.lastName} ({t.department})
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Subject Name"
                                        value={customSubjectName}
                                        onChange={(e) => setCustomSubjectName(e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                                <Button size="small" onClick={() => { setOpenManualAssignForm(false); setSelectedPeriodToAssign(null); }}>
                                    Cancel
                                </Button>
                                <Button size="small" variant="contained" onClick={() => handleManualAssign(selectedPeriodToAssign.id)}>
                                    Send Request
                                </Button>
                            </Box>
                        </Paper>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenManageSubDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Print Alternative Arrangement Dialog */}
            <Dialog open={printDialog} onClose={() => setPrintDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Alternative Arrangement
                    <Button
                        variant="contained"
                        startIcon={<PrintIcon />}
                        sx={{ float: 'right' }}
                        onClick={() => {
                            const content = arrangementRef.current;
                            const printWindow = window.open('', '_blank');
                            printWindow.document.write(`
                                <html>
                                <head>
                                    <title>Alternative Arrangement</title>
                                    <style>
                                        @page { size: A4 portrait; margin: 10mm; }
                                        body { font-family: Arial, sans-serif; }
                                        table { border-collapse: collapse; width: 100%; }
                                        th, td { border: 1px solid #000; padding: 6px 8px; font-size: 12px; }
                                        th { background: #f5f5f5; }
                                    </style>
                                </head>
                                <body>${content.innerHTML}</body>
                                </html>
                            `);
                            printWindow.document.close();
                            printWindow.print();
                        }}
                    >
                        Print
                    </Button>
                </DialogTitle>
                <DialogContent>
                    <AlternativeArrangement
                        ref={arrangementRef}
                        leaveData={printLeaveData}
                        substitutions={printLeaveData?.substitutions || []}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPrintDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LeaveManagement;

